import {
    Accordion,
    AccordionDetails,
    Box,
    Skeleton,
    Typography,
    alpha,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getSelectedCatalogItemsTotal } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { getLocalCategoriesList } from 'entities/Category';
import { getSelectedOrder } from 'entities/Order/model/selectors/ordersSelectors';
import { UserRole, getUserRole } from 'entities/User';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { OrderProposalFilter } from 'features/OrderProposalFilter';
import { sortOrderViewSummaryEntries } from 'pages/OrderViewPage/lib/sortOrderViewData';
import { getCurrentOrderSummary } from 'pages/OrderViewPage/model/selectors/orderViewPageSelectors';
import { AccordionHeader } from 'shared/ui/AccordionHeader/AccordionHeader';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';

interface OrderViewSummaryDetailsProps {
    summaryByCategories: Record<
        string,
        {
            count: number;
            cost: number;
        }
    >;
    isLoadingSummaryDetails: boolean;
    isInteractionStatus: boolean;
}

export const OrderViewSummaryDetails = ({
    summaryByCategories,
    isLoadingSummaryDetails,
    isInteractionStatus,
}: OrderViewSummaryDetailsProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(!isInteractionStatus);
    }, [isInteractionStatus, setExpanded]);

    const currentUserRole = useSelector(getUserRole);
    const isTechnicianUser =
        currentUserRole === UserRole.MAINTENANCE_TECHNICIAN;

    const totalCategories = Object.values(summaryByCategories).length;

    const categories = useSelector(getLocalCategoriesList);
    const { totalPrice, hasLoadingPrices } = useSelector(
        getCurrentOrderSummary,
    );
    const canSeePrices = useSelector(getUserCanSeePrices);
    const totalItemsCount = useSelector(getSelectedCatalogItemsTotal);
    const selectedOrder = useSelector(getSelectedOrder);
    const sortedSummaryEntries = useMemo(
        () => sortOrderViewSummaryEntries(summaryByCategories, categories),
        [summaryByCategories, categories],
    );

    const handleChangeExpand = () => {
        setExpanded((prev) => !prev);
    };

    return (
        <Box sx={{ my: '16px' }} data-testid="order-summary">
            <Accordion
                sx={(theme) => ({
                    bgcolor: alpha(theme.palette.grey[500], 0.5),
                    borderRadius: '12px',
                })}
                defaultExpanded={false}
                expanded={expanded}
                onChange={handleChangeExpand}
            >
                <AccordionHeader title={t('Order summary')} />
                <AccordionDetails sx={{ p: '12px' }}>
                    {!isLoadingSummaryDetails ? (
                        <Box>
                            <Box
                                mb="8px"
                                display="flex"
                                justifyContent="space-between"
                                data-testid="order-summary-total"
                            >
                                <Box display="inline-flex" gap="4px">
                                    <Typography
                                        variant="subtitle2"
                                        data-testid="order-summary-total-items-qty"
                                    >
                                        {t('Total Items')} ({totalItemsCount})
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        data-testid="order-summary-total-categories-qty"
                                    >
                                        {t('in Categories')} ({totalCategories})
                                    </Typography>
                                </Box>
                                {canSeePrices && (
                                    <OrderPriceDisplay
                                        width="70px"
                                        testId="order-summary-total-price"
                                        typography="subtitle2"
                                        price={totalPrice}
                                        order={selectedOrder}
                                        orderHasLoadingPrices={hasLoadingPrices}
                                    />
                                )}
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap="8px"
                            >
                                {sortedSummaryEntries.map(([id, info]) => (
                                    <Box
                                        key={id}
                                        display="flex"
                                        justifyContent="space-between"
                                        data-testclass="order-summary-category"
                                    >
                                        <Box display="flex" gap="4px">
                                            <Typography data-testclass="order-summary-category-name">
                                                {categories?.[id]?.title}
                                            </Typography>
                                            <Typography
                                                color={(theme) =>
                                                    alpha(
                                                        theme.palette.common
                                                            .black,
                                                        0.5,
                                                    )
                                                }
                                            >
                                                {categories?.[id]?.glCode
                                                    ? ` | ${categories?.[id]?.glCode}`
                                                    : null}
                                            </Typography>
                                        </Box>
                                        {canSeePrices && (
                                            <OrderPriceDisplay
                                                width="60px"
                                                containerHeight="24px"
                                                testClass="order-summary-category-price"
                                                price={info.cost}
                                                order={selectedOrder}
                                                orderHasLoadingPrices={
                                                    hasLoadingPrices
                                                }
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                            {!isTechnicianUser && <OrderProposalFilter />}
                        </Box>
                    ) : (
                        <>
                            <Skeleton width="240px" height="32px" />
                            <Skeleton width="80px" height="32px" />
                            <Skeleton width="180px" height="20px" />
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
